import { t } from '@/utils/i18n'
import { useState, useRef, useEffect } from 'react'
import { useUpdateEffect } from 'ahooks'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CloseCircleFill } from 'antd-mobile-icons'
import SortModal from '@/components/SortModal/SortModal'
import ProductList from '@/components/ProductList/ProductList'
import { utils, productUtil, storageUtil } from '@/utils'
import useCorpSettingStore from '@/stores/corp-setting'
import type { SortItem, RequestParams } from '@/types/product'
import styles from './Search.module.less'

// 导入图片
import iconSortGray from '@/assets/img/icon/sort_gray.png'
import iconDelete from '@/assets/img/icon/delete.png'
import iconSearch from '@/assets/img/icon/search.png'

const { debounce, isDisableBodyScroll, cloneDeep } = utils

const { getLocalStorage, setLocalStorage, removeLocalStorage } = storageUtil

function Search() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const corpSettingStore = useCorpSettingStore()
  // const [scanCode, setScanCode] = useState('')
  const [searchText, setSearchText] = useState(searchParams.get('text') || '')
  const [showSortModal, setShowSortModal] = useState(false)
  const [sortIndex, setSortIndex] = useState(-1)
  const [sortKey, setSortKey] = useState('')
  const [sortDirection, setSortDirection] = useState('DESC')
  const [searchHistory, setSearchHistory] = useState<string[]>([])
  const [searchBarHeight, setSearchBarHeight] = useState('')
  // 商品列表搜索分类/推荐相关参数
  const [filter, setFilter] = useState<RequestParams>({
    otherType: 0, // 买过 传1
    productStatus: 0, // 推荐传2
    recommendType: 0, // 推荐时传 推荐类型：1新品 2爆品 4促销 99多货品分享
    categoryId: '0',
    categoryLevel: 0,
    combineType: 0,
    customerId: '0',
    interfaceVersion: 1,
    onScan: false,
    sortDirection: '1',
    sortKey: '5',
    unionMall: 0,
    minPrice: '',
    maxPrice: '',
  })
  const options = productUtil.initSortOptions(
    corpSettingStore.corpSetting.cloudProductSort,
    corpSettingStore.corpSetting.salesOn,
    corpSettingStore.corpSetting.salesSlipOn,
  ) // 初始化排序选项
  const searchBar = useRef<HTMLDivElement>(null)
  const inputWrap = useRef<HTMLInputElement>(null)

  const handleSearchByQuery = debounce((e: any) => {
    setSearchText(e.target.value)
  }, 300)

  function saveSearchHistory() {
    const keyWord = searchText.trim()

    if (!keyWord) return

    const searchHistoryCache = getLocalStorage('searchHistory', true)
    let history = searchHistoryCache ? searchHistoryCache : []
    const index = history.findIndex((item: string) => item === keyWord)

    if (index === -1) {
      history.unshift(keyWord)
    } else {
      // 最近搜索置顶
      history.unshift(history.splice(index, 1)[0])
    }

    // 最多显示十条搜索记录
    if (history.length > 10) {
      history = history.slice(0, 10)
    }

    setSearchHistory(history)
    setLocalStorage('searchHistory', history)
  }

  function resetData(isClearText = true) {
    if (isClearText) {
      setSearchText('')
    }
  }

  function clearQuery() {
    if (searchText) {
      if (inputWrap.current) {
        inputWrap.current.value = ''
      }

      saveSearchHistory()
      resetData()
      handleInputFocus()
    }
  }

  function handleInputFocus() {
    if (inputWrap.current) {
      inputWrap.current.focus()
    }
  }

  function returnBack() {
    navigate(-1)
  }

  // 切换排序
  function toggleShowSortModal() {
    setShowSortModal(!showSortModal)
  }

  // 排序筛选改变
  function handleChangeSort(sortItem: SortItem, index: number) {
    setSortIndex(index)
    setSortKey(sortItem.key)
    setSortDirection(sortItem.sortDirection || 'DESC')
    toggleShowSortModal()
  }

  function clearSearchHistory() {
    setSearchHistory([])
    removeLocalStorage('searchHistory')
  }

  function tapHistory(text: string) {
    setSearchText(text)
  }

  const HistoryItem = () => {
    return searchHistory.map((item, index) => {
      return (
        <div
          key={index}
          data-query={item}
          onClick={() => tapHistory(item)}
          className={styles['history-item']}
        >
          <span>{item}</span>
        </div>
      )
    })
  }

  // 初始化
  function init() {
    // 加载历史记录
    const searchHistoryCache = getLocalStorage('searchHistory', true)

    setSearchHistory(searchHistoryCache || [])
    setSortKey(options.sortActive.activeKey)
    setSortIndex(options.sortActive.activeIndex || 1)
    setSortDirection(options.sortActive.sortDirection || 'DESC')
  }

  useEffect(() => {
    document.title = t('搜索')
    if (searchBar.current) {
      setSearchBarHeight(searchBar.current.offsetHeight + 'px')
    }

    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // 禁用 body 滚动
    isDisableBodyScroll(showSortModal)
  }, [showSortModal])

  // 设置请求参数
  useUpdateEffect(() => {
    filter.combineType = 0
    filter.sortKey = sortKey
    filter.sortDirection = sortDirection === 'ASC' ? '0' : '1'
    filter.searchText = searchText

    setFilter(cloneDeep(filter))
  }, [sortDirection, sortKey, searchText])

  return (
    <div className={styles['search-page']}>
      {/* 搜索框 */}
      <div className={styles['kp-search-bar']} ref={searchBar}>
        {/* 排序 */}
        <img
          className={styles['icon-sort']}
          src={iconSortGray}
          onClick={toggleShowSortModal}
        />

        <div className={styles['kp-search-wrapper']}>
          <img className={styles['title-search-icon']} src={iconSearch} />
          {/* <SearchOutline
            className={styles['kp-icon']}
            fontSize={16}
            color="#999"
          /> */}
          <input
            className={styles['input'] + ' ep'}
            ref={inputWrap}
            type="text"
            value={searchText}
            onChange={(e: any) => {
              setSearchText(e.target.value)
            }}
            onFocus={handleInputFocus}
            maxLength={40}
            placeholder={t('支持货名/属性/条码')}
            placeholder-class="input"
            onInput={handleSearchByQuery}
            onBlur={saveSearchHistory}
          />

          {searchText ? (
            <CloseCircleFill
              fontSize={12}
              className={styles['kp-icon'] + ' ' + styles['clear']}
              onClick={clearQuery}
            />
          ) : (
            ''
          )}
        </div>
        <div className={styles['kp-search-cancel']} onClick={returnBack}>
          {t('取消')}
        </div>
      </div>

      {/* 排序弹窗 */}
      {showSortModal ? (
        <SortModal
          top={searchBarHeight}
          position={'fixed'}
          sortIndex={sortIndex}
          sortDirection={sortDirection}
          onToggleShowSortModal={toggleShowSortModal}
          onChangeSort={handleChangeSort}
        />
      ) : (
        ''
      )}

      {/* 历史搜索记录 */}
      {!searchText && searchHistory.length > 0 ? (
        <div className={styles['search-history-container']}>
          <div className={styles['history-title']}>
            <span>{t('最近搜索')}</span>
            <img
              className={styles['clear-history']}
              onClick={clearSearchHistory}
              src={iconDelete}
            />
          </div>

          <div className={styles['history-content']}>
            <HistoryItem />
          </div>
        </div>
      ) : (
        ''
      )}

      {searchText ? (
        <ProductList
          listMode={true}
          requestParams={filter}
          isFilterPage={false}
        />
      ) : (
        ''
      )}
    </div>
  )
}

export default Search
